import React, { useContext } from 'react';

import { ComponentHealthWrapper } from './ComponentHealthWrapper/ComponentHealthWrapper';
import type { InternalContextValue } from './types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

export type ComponentHealthWrapFn = <T extends {}>(
  component: React.ComponentType<T>,
) => React.FunctionComponent<T & { componentHealthNameOverride?: string; errorComponent?: React.ComponentType<{}>; forceErrorUIOnTimeout?: boolean; timeout?: number }>;

export interface ComponentHealthCallbacks {
  readonly onLoadComplete: () => void;
  readonly onLoadFailure: (reason: unknown, context?: object, forceErrorUI?: boolean) => void;
}

export function createComponentHealth(
  name: string,
): [wrapWithComponentHealth: ComponentHealthWrapFn, useComponentHealth: (extraInfo?: Record<string, unknown>) => ComponentHealthCallbacks] {
  const context = React.createContext<InternalContextValue | undefined>(undefined);

  return [
    <T extends { componentHealthNameOverride?: string; errorComponent?: React.ComponentType<{}>; forceErrorUIOnTimeout?: boolean; timeout?: number; slowLogTimeout?: number }>(
      WrappedComponent: React.ComponentType<T>,
    ) => {
      return ({ componentHealthNameOverride, errorComponent, forceErrorUIOnTimeout, timeout, slowLogTimeout, ...restProps }: T) => (
        <ComponentHealthWrapper
          name={componentHealthNameOverride || name}
          internalContext={context}
          errorComponent={errorComponent}
          forceErrorUIOnTimeout={forceErrorUIOnTimeout}
          timeout={timeout}
          slowLogTimeout={slowLogTimeout}
        >
          <WrappedComponent {...(restProps as T)} />
        </ComponentHealthWrapper>
      );
    },
    extraInfo => {
      const contextValue = useContext(context);

      if (!contextValue) {
        throw new Error(`useComponentHealth must be used in a component wrapped with wrapWithComponentHealth for '${name}'!`);
      }

      const { onLoadComplete, onLoadFailure, onRegisterWithProvider } = contextValue;

      onRegisterWithProvider();

      const onLoadFailureWithContext = useEventCallback((reason: unknown, context?: object) => {
        onLoadFailure(reason, { ...extraInfo, ...context });
      });

      return {
        onLoadComplete,
        onLoadFailure: onLoadFailureWithContext,
      };
    },
  ];
}
